import { SearchableSelect } from "@/components/SearchableSelect";
import React, { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, Send, Sparkles, User } from "lucide-react";
import api from "@/lib/apiClient";

const CHIPS = [
  "Quali DURC scadono nei prossimi 30 giorni?",
  "Mostrami tutte le imprese con DURC scaduto.",
  "Quali idoneità sanitarie scadono a breve?",
  "Quali documenti mancano alle imprese distaccanti?",
];

export function AiAssistant({ open, onOpenChange }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Ciao! Sono l'assistente AI di CantiereDoc. Posso interrogare l'archivio documentale per te. Chiedimi di scadenze, documenti mancanti, imprese o lavoratori." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const [sites, setSites] = useState([]), [docs, setDocs] = useState([]), [siteId, setSiteId] = useState("all"), [docId, setDocId] = useState("all");
  useEffect(() => { if (open) api.get("/sites").then(r => setSites(r.data)).catch(() => {}); }, [open]);
  useEffect(() => { if (open) api.get("/documents",{params:siteId !== "all" ? {site_id:siteId} : {}}).then(r => setDocs(r.data)).catch(() => {}); setDocId("all"); }, [open, siteId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { question, site_id: siteId === "all" ? null : siteId, document_id: docId === "all" ? null : docId }, { timeout: 660000 });
      setMessages((m) => [...m, { role: "assistant", text: data.answer, sources: data.sources }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Errore nel contattare l'assistente. Riprova." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="ai-assistant-drawer">
        <SheetHeader className="px-5 py-4 border-b bg-slate-900 text-white">
          <SheetTitle className="flex items-center gap-2 text-white font-head">
            <Sparkles className="h-5 w-5 text-blue-400" /> Assistente AI
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center ${m.role === "user" ? "bg-blue-600" : "bg-slate-800"}`}>
                {m.role === "user" ? <User className="h-4 w-4 text-white" /> : <Bot className="h-4 w-4 text-blue-300" />}
              </div>
              <div className={`rounded-xl px-3 py-2 text-sm max-w-[80%] whitespace-pre-wrap ${m.role === "user" ? "bg-blue-600 text-white" : "bg-slate-100 dark:bg-slate-800"}`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && <div className="text-xs text-muted-foreground pl-10 animate-pulse">L'assistente sta analizzando l'archivio…</div>}
          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {CHIPS.map((c) => (
                <button key={c} onClick={() => send(c)} data-testid="ai-chip"
                  className="text-xs rounded-full border border-slate-300 dark:border-slate-700 px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors">
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t p-3 space-y-2">
          <SearchableSelect width="w-full" value={siteId} onValueChange={setSiteId} options={[{value:"all",label:"Tutti i cantieri"},...sites.map(s => ({value:s.id,label:s.denominazione}))]} />
          <SearchableSelect width="w-full" value={docId} onValueChange={setDocId} options={[{value:"all",label:"Ricerca automatica (max 5 documenti)"},...docs.map(d => ({value:d.id,label:`${d.tipo_documento} · ${d.original_filename}`}))]} />
          {messages.filter(m => m.sources?.length).slice(-1).flatMap(m => m.sources).map(d => <a key={d.id} className="block text-xs text-blue-600" href={`/documenti/${d.id}`} target="_blank" rel="noreferrer">Fonte: {d.name} · {d.filename}</a>)}
        </div>
        <div className="border-t p-3 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Scrivi una domanda…" data-testid="ai-chat-input" />
          <Button onClick={() => send()} disabled={loading} data-testid="btn-ai-chat-send">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
